import React, { useState, useRef, useEffect } from 'react';
import useNotifications from '../../hooks/useNotifications';
import useTheme from '../../hooks/useTheme';
import './NotificationCenter.css';

// SVG icons for different notification types
const icons = {
  success: (
    <svg width="18" height="18" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
      <circle cx="12" cy="12" r="11" stroke="#4CAF50" strokeWidth="2"/>
      <path d="M7 12L10 15L17 8" stroke="#4CAF50" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
    </svg>
  ),
  error: (
    <svg width="18" height="18" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
      <circle cx="12" cy="12" r="11" stroke="#F44336" strokeWidth="2"/>
      <path d="M8 8L16 16M8 16L16 8" stroke="#F44336" strokeWidth="2" strokeLinecap="round"/>
    </svg>
  ),
  warning: (
    <svg width="18" height="18" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
      <path d="M12 2L2 21H22L12 2Z" stroke="#FF9800" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
      <path d="M12 16V16.01M12 8V12" stroke="#FF9800" strokeWidth="2" strokeLinecap="round"/>
    </svg>
  ),
  info: (
    <svg width="18" height="18" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
      <circle cx="12" cy="12" r="11" stroke="#2196F3" strokeWidth="2"/>
      <path d="M12 7V7.01M12 11V17" stroke="#2196F3" strokeWidth="2" strokeLinecap="round"/>
    </svg>
  ),
  order: (
    <svg width="18" height="18" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
      <rect x="3" y="3" width="18" height="18" rx="2" stroke="#64B5F6" strokeWidth="2"/>
      <path d="M7 12L10 15L17 8" stroke="#64B5F6" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
    </svg>
  ),
  system: (
    <svg width="18" height="18" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
      <circle cx="12" cy="12" r="11" stroke="#9E9E9E" strokeWidth="2"/>
      <path d="M12 7V7.01M12 11V17" stroke="#9E9E9E" strokeWidth="2" strokeLinecap="round"/>
    </svg>
  ),
  default: (
    <svg width="18" height="18" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
      <circle cx="12" cy="12" r="11" stroke="#757575" strokeWidth="2"/>
      <path d="M12 7V7.01M12 11V17" stroke="#757575" strokeWidth="2" strokeLinecap="round"/>
    </svg>
  )
};

/**
 * Format relative time for notifications
 * @param {string} timestamp - ISO timestamp string
 * @returns {string} - Formatted relative time
 */
const formatTime = (timestamp) => {
  if (!timestamp) return '';
  
  try {
    const date = new Date(timestamp);
    const now = new Date();
    const diffMs = now - date;
    
    // Convert to seconds
    const diffSec = Math.floor(diffMs / 1000);
    
    // Less than a minute
    if (diffSec < 60) {
      return 'just now';
    }
    
    // Less than an hour
    if (diffSec < 3600) {
      const minutes = Math.floor(diffSec / 60);
      return `${minutes} minute${minutes > 1 ? 's' : ''} ago`;
    }
    
    // Less than a day
    if (diffSec < 86400) {
      const hours = Math.floor(diffSec / 3600);
      return `${hours} hour${hours > 1 ? 's' : ''} ago`;
    }
    
    // Less than a week
    if (diffSec < 604800) {
      const days = Math.floor(diffSec / 86400);
      return `${days} day${days > 1 ? 's' : ''} ago`;
    }
    
    // Use simple date format
    return date.toLocaleDateString();
  } catch (error) {
    console.error('Error formatting timestamp:', error);
    return timestamp;
  }
};

/**
 * Individual notification item
 */
const NotificationItem = ({ notification, onMarkAsRead, onRemove }) => {
  const { id, message, type, status, timestamp, isRead } = notification;
  
  // Determine notification type
  const notificationType = type === 'order' || 
                           (message && (message.toLowerCase().includes('order') || 
                                        message.toLowerCase().includes('position'))) 
    ? 'order' 
    : type || 'default';
  
  // Get the icon based on notification type
  const icon = icons[notificationType] || icons.default;
  
  // Format the time
  const formattedTime = formatTime(timestamp);
  
  return (
    <div className={`notification-item ${isRead ? 'read' : 'unread'}`}>
      <div className="notification-icon">{icon}</div>
      <div className="notification-content">
        <div className="notification-message">{message}</div>
        <div className="notification-meta">
          <span className="notification-time">{formattedTime}</span>
          {status && <span className="notification-status">{status}</span>}
        </div>
      </div>
      <div className="notification-actions">
        {!isRead && (
          <button 
            className="mark-read-button" 
            onClick={() => onMarkAsRead(id)}
            aria-label="Mark as read"
          >
            <svg width="16" height="16" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
              <circle cx="12" cy="12" r="11" stroke="currentColor" strokeWidth="2"/>
              <path d="M7 12L10 15L17 8" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
            </svg>
          </button>
        )}
        <button 
          className="remove-button" 
          onClick={() => onRemove(id)}
          aria-label="Remove notification"
        >
          <svg width="16" height="16" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
            <circle cx="12" cy="12" r="11" stroke="currentColor" strokeWidth="2"/>
            <path d="M8 8L16 16M8 16L16 8" stroke="currentColor" strokeWidth="2" strokeLinecap="round"/>
          </svg>
        </button>
      </div>
    </div>
  );
};

/**
 * Bell icon with badge for notification toggle
 */
const NotificationBell = ({ count, onClick, isOpen }) => (
  <button 
    className={`notification-bell ${isOpen ? 'active' : ''} ${count > 0 ? 'has-notifications' : ''}`}
    onClick={onClick}
    aria-label={`Notifications ${count > 0 ? `(${count} unread)` : ''}`}
  >
    <svg width="24" height="24" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
      <path d="M12 4c-4.4 0-8 3.6-8 8v4l-2 2h20l-2-2v-4c0-4.4-3.6-8-8-8zm0 0V2M8 18c0 2.2 1.8 4 4 4s4-1.8 4-4" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
    </svg>
    {count > 0 && <span className="notification-badge">{count > 99 ? '99+' : count}</span>}
  </button>
);

/**
 * The main notification center component
 */
const NotificationCenter = () => {
  const { theme } = useTheme();
  const [isOpen, setIsOpen] = useState(false);
  const notificationRef = useRef(null);
  
  // Get notifications using our custom hook
  const { 
    notifications, 
    loading, 
    error, 
    unreadCount,
    fetchNotifications, 
    markAsRead, 
    markAllAsRead, 
    removeNotification 
  } = useNotifications();
  
  // Toggle notification center
  const toggleNotifications = () => {
    setIsOpen(prev => !prev);
  };
  
  // Handle mark as read
  const handleMarkAsRead = async (id) => {
    await markAsRead(id);
  };
  
  // Handle mark all as read
  const handleMarkAllAsRead = async () => {
    await markAllAsRead();
  };
  
  // Handle remove notification
  const handleRemoveNotification = async (id) => {
    await removeNotification(id);
  };
  
  // Handle refresh
  const handleRefresh = () => {
    fetchNotifications();
  };
  
  // Close notification panel when clicking outside
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (notificationRef.current && !notificationRef.current.contains(event.target)) {
        setIsOpen(false);
      }
    };
    
    if (isOpen) {
      document.addEventListener('mousedown', handleClickOutside);
    }
    
    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, [isOpen]);
  
  // Apply theme class
  const themeClass = theme ? `theme-${theme}` : '';
  
  return (
    <div className={`notification-center ${themeClass}`} ref={notificationRef}>
      <NotificationBell 
        count={unreadCount} 
        onClick={toggleNotifications} 
        isOpen={isOpen}
      />
      
      {isOpen && (
        <div className="notification-panel">
          <div className="notification-header">
            <h3 className="notification-title">Notifications</h3>
            <div className="notification-actions-header">
              {unreadCount > 0 && (
                <button 
                  className="mark-all-read-button" 
                  onClick={handleMarkAllAsRead}
                >
                  Mark all as read
                </button>
              )}
              <button 
                className="refresh-button" 
                onClick={handleRefresh}
                disabled={loading}
              >
                <svg 
                  className={loading ? 'loading' : ''} 
                  width="16" 
                  height="16" 
                  viewBox="0 0 24 24" 
                  fill="none" 
                  xmlns="http://www.w3.org/2000/svg"
                >
                  <path d="M21.5 2v6h-6M2.5 22v-6h6M2 11.5a10 10 0 0 1 18.8-4.3M22 12.5a10 10 0 0 1-18.8 4.3" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
                </svg>
              </button>
            </div>
          </div>
          
          <div className="notification-list">
            {error && (
              <div className="notification-error">
                <p>{error}</p>
                <button onClick={handleRefresh}>Retry</button>
              </div>
            )}
            
            {loading && notifications.length === 0 && (
              <div className="notification-loading">
                <div className="loading-spinner"></div>
                <p>Loading notifications...</p>
              </div>
            )}
            
            {!loading && notifications.length === 0 && !error && (
              <div className="notification-empty">
                <svg width="48" height="48" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                  <path d="M12 4c-4.4 0-8 3.6-8 8v4l-2 2h20l-2-2v-4c0-4.4-3.6-8-8-8zm0 0V2M8 18c0 2.2 1.8 4 4 4s4-1.8 4-4" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
                </svg>
                <p>No notifications</p>
              </div>
            )}
            
            {notifications.map(notification => (
              <NotificationItem 
                key={notification.id} 
                notification={notification} 
                onMarkAsRead={handleMarkAsRead}
                onRemove={handleRemoveNotification}
              />
            ))}
          </div>
        </div>
      )}
    </div>
  );
};

export default NotificationCenter; 